import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../utils/supabase';
import Icon from '../../../components/AppIcon';

const FriendsPlaying = ({ gameId }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [friendsPlaying, setFriendsPlaying] = useState([]);
  const [totalFriendsPlaying, setTotalFriendsPlaying] = useState(0);

  useEffect(() => {
    if (user?.id && gameId) {
      loadFriendsPlaying();

      // Set up realtime subscription for presence changes
      const presenceSubscription = supabase
        .channel(`game_${gameId}_presence`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_presence',
            filter: `game_id=eq.${gameId}`
          },
          () => {
            loadFriendsPlaying();
          }
        )
        .subscribe();

      return () => {
        presenceSubscription.unsubscribe();
      };
    }
  }, [user, gameId]);

  const loadFriendsPlaying = async () => {
    try {
      setLoading(true);

      // Get user's friends
      const { data: friendships, error: friendshipsError } = await supabase
        .from('friendships')
        .select('friend_id')
        .eq('user_id', user.id);

      if (friendshipsError) throw friendshipsError;

      if (!friendships || friendships.length === 0) {
        setFriendsPlaying([]);
        setTotalFriendsPlaying(0);
        return;
      }

      const friendIds = friendships.map(f => f.friend_id);

      // Get friends who are currently playing this game
      const { data: presenceData, error: presenceError } = await supabase
        .from('user_presence')
        .select(`
          user_id,
          status,
          custom_status,
          joinable,
          last_seen
        `)
        .eq('game_id', gameId)
        .eq('status', 'playing')
        .in('user_id', friendIds);

      if (presenceError) throw presenceError;

      if (!presenceData || presenceData.length === 0) {
        setFriendsPlaying([]);
        setTotalFriendsPlaying(0);
        return;
      }

      const playingFriendIds = presenceData.map(p => p.user_id);

      // Get friend profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, avatar_url')
        .in('id', playingFriendIds);

      if (profilesError) throw profilesError;

      // Combine presence and profile data
      const presenceMap = {};
      presenceData.forEach(p => {
        presenceMap[p.user_id] = {
          customStatus: p.custom_status,
          joinable: p.joinable,
          lastSeen: p.last_seen
        };
      });

      const friendsList = (profiles || []).map(profile => ({
        id: profile.id,
        fullName: profile.full_name,
        avatarUrl: profile.avatar_url,
        ...presenceMap[profile.id]
      }));

      setFriendsPlaying(friendsList.slice(0, 5)); // Show max 5
      setTotalFriendsPlaying(friendsList.length);

    } catch (err) {
      console.error('Error loading friends playing:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Now';

    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  if (!user) return null;
  if (loading) return null;
  if (totalFriendsPlaying === 0) return null;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Users" size={20} />
          Friends Playing
        </h3>
        {totalFriendsPlaying > 5 && (
          <Link
            to="/user-profile-settings?tab=friends"
            className="text-sm text-primary hover:underline"
          >
            View all ({totalFriendsPlaying})
          </Link>
        )}
      </div>

      <div className="space-y-3">
        {friendsPlaying.map(friend => (
          <div key={friend.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
            <div className="relative">
              <img
                src={friend.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(friend.fullName)}&background=6366f1&color=fff`}
                alt={friend.fullName}
                className="w-10 h-10 rounded-full"
              />
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-card" />
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground truncate">{friend.fullName}</p>
              {friend.customStatus ? (
                <p className="text-sm text-muted-foreground truncate">{friend.customStatus}</p>
              ) : (
                <p className="text-sm text-blue-500">Playing now</p>
              )}
            </div>

            {friend.joinable && (
              <div className="flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-500 text-xs font-medium rounded">
                <Icon name="UserPlus" size={12} />
                <span>Joinable</span>
              </div>
            )}
          </div>
        ))}
      </div>

      <Link
        to="/user-profile-settings?tab=friends"
        className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg transition-colors font-medium"
      >
        <Icon name="Users" size={16} />
        Invite More Friends
      </Link>
    </div>
  );
};

export default FriendsPlaying;
